import React, { useEffect, useState, useMemo } from "react";
import { createCityAdmin } from "../Services/SuperAdminApi";
import { getCities } from "../Services/citiesApi";
import Select from "react-tailwindcss-select";
import { useTranslation } from "react-i18next";
import { role } from "../Constants/role";

const EMAIL_REGEXP =
  /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const CreateAdmin = ({ onSuccess, isSuperAdmin }) => {
  const [email, setEmail] = useState("");
  const [citiesList, setCitiesList] = useState([]);
  const [errors, setErrors] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null); // For Super City Admin
  const [selectedCities, setSelectedCities] = useState([]); // For City Admin
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const handleChange = (e) => {
    setEmail(e.target.value.trim());
  };

  const validate = () => {
    const newErrors = {};
    if (!email.trim) newErrors.email = t("pleaseEnterEmailAddress");
    if (!EMAIL_REGEXP.test(email)) newErrors.email = t("pleaseEnterValidEmail");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!validate()) return;
    try {
      let permissionData = {
        roleId: selectedRole,
        email: email,
      };
      if (selectedRole === role.superCityAdmin && isSuperAdmin) {
        permissionData.cityId = selectedCity?.value;
      }
      // If City Admin, send multiple citiesIds
      else if (selectedRole === role.TerminalAdmin && isSuperAdmin) {
        permissionData.cityId = selectedCities.map((c) => c.value);
      }
      const { data } = await createCityAdmin(permissionData);
      onSuccess(data.status);
    } catch (e) {
      setErrors({ message: e?.response?.data?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCities().then((citiesResponse) => {
      setCitiesList(
        citiesResponse.data.data?.map((cities) => ({
          value: cities.id,
          label: cities.name,
        }))
      );
    });
  }, []);

  const handleRoleChange = (option) => {
    setSelectedRole(Number(option.value));
    setSelectedCities([]);
    setSelectedCity(null);
  };

  const roleOptions = isSuperAdmin
    ? [
        { value: role.TerminalAdmin, label: t("cityAdmins") || "City Admin" },
        {
          value: role.superCityAdmin,
          label: t("superCityAdmins") || "Super City Admin",
        },
        // { value: role.contentCreator, label: t("citizen") },
      ]
    : [
        { value: role.TerminalAdmin, label: t("cityAdmins") || "City Admin" },
        // { value: role.contentCreator, label: t("citizen") },
      ];

  const handleCitiesChange = (value) => {
    console.log(value);
    setSelectedCities(value);
  };

  const handleCityChange = (value) => {
    setSelectedCity(value);
  };

  // Filter out already selected cities from the options
  const availableCitiesOptions = useMemo(() => {
    if (!citiesList || citiesList.length === 0) return [];
    
    // Get IDs of already selected cities
    const selectedCityIds = selectedCities.map((city) => city.value);
    
    // Filter out already selected cities
    return citiesList.filter(
      (city) => !selectedCityIds.includes(city.value)
    );
  }, [citiesList, selectedCities]);

  return (
    <div className="max-w-md mx-auto bg-white rounded-2xl max-h-[80vh] overflow-y-auto" 
    onMouseDown={(e) => {
        e.stopPropagation(); // ✅ THIS IS THE KEY
        }}
    >
      <h2 className="text-2xl font-bold mb-4 text-center">
        {t("invite_user")}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4 p-4">
        <div className="max-w-sm mt-6 mb-4">
          <label className="block font-medium mb-2">
            {t("email")} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={email}
            onChange={handleChange}
            className="w-full mt-1 px-4 py-2 border rounded-xl focus:outline-none focus:ring focus:ring-blue-300"
          />
          {errors.email && (
            <p className="text-sm text-red-500">{errors.email}</p>
          )}
        </div>
        <div className="max-w-sm mt-6 mb-4">
          <label className="block font-medium mb-2">
            {t("role") || "New Role"}{" "}
            {/* <span className="text-red-500">*</span> */}
            <span className="text-red-500">*</span>
          </label>

          <Select
            primaryColor="indigo"
            value={
              roleOptions.find((opt) => Number(opt.value) === selectedRole) ||
              null
            }
            onChange={handleRoleChange}
            options={roleOptions}
            classNames={{
              menu: "max-h-40 overflow-y-auto",
              list: "space-y-1",
              option: "py-1 px-2",
            }}
          />
        </div>
        {/* <div class="max-w-sm mt-6 mb-4">
          <label for="countries" class="block font-medium">
            {t("selectCity")}
          </label>
          <Select
            primaryColor={"indigo"}
            value={cities}
            isMultiple={isSuperAdmin}
            onChange={handleCitiesChange}
            options={citiesList}
            classNames={{
              menu: "max-h-40 overflow-y-auto", // LIMIT dropdown height to ~12rem
              list: "space-y-1", // optional: spacing between options
              option: "py-1 px-2", // compact option height
            }}
          />
        </div> */}

        {selectedRole === role.TerminalAdmin && isSuperAdmin && (
          <div>
            <label className="block font-medium mb-2">
              {t("selectCity") || "Select Cities"}{" "}
              <span className="text-red-500">*</span>
            </label>
            <Select
              primaryColor="indigo"
              value={selectedCities}
              isMultiple
              onChange={handleCitiesChange}
              options={availableCitiesOptions}
              placeholder={
             t("select_assign_city") || "Select cities to assign"
              }
              classNames={{
                menu: "max-h-40 overflow-y-auto",
                list: "space-y-1",
                option: "py-1 px-2",
              }}
              noOptionsMessage={
                availableCitiesOptions?.length === 0 ? t("noDataFound") : null
              }
            />
            <p className="text-xs text-gray-500 mt-1">
              {t("city_admin_manage_multi_city") ||
                "City Admins can manage multiple cities"}
            </p>
          </div>
        )}

        {selectedRole === role.superCityAdmin && isSuperAdmin && (
          <div>
            <label className="block font-medium mb-2">
              {t("selectCity") || "Select City"}{" "}
              <span className="text-red-500">*</span>
            </label>
            <Select
              primaryColor="indigo"
              value={selectedCity}
              onChange={handleCityChange}
              options={citiesList}
              placeholder={t("select_assign_city") || "Select cities to assign"}
              classNames={{
                menu: "max-h-40 overflow-y-auto",
                list: "space-y-1",
                option: "py-1 px-2",
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t("super_city_admin") || "Super City Admins manage one city"}
            </p>
          </div>
        )}

        {/* <div>
          <label className="block font-medium mb-2">
            {t("new_role") || "New Role"}{" "}
            <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedRole || ""}
            onChange={handleRoleChange}
            className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring focus:ring-blue-300"
            required
          >
            <option value="">{t("select_role") || "Select Role"}</option>
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {t("change_role") || "Change role to Citizen or Super City Admin"}
          </p>
        </div> */}
        {errors.message && (
          <p className="text-sm text-red-500 text-center">{errors.message}</p>
        )}
        <button
          type="submit"
          disabled={
            loading ||
            !email ||
            !selectedRole ||
            (selectedRole === role.superCityAdmin &&
              isSuperAdmin &&
              !selectedCity) ||
            (selectedRole === role.TerminalAdmin &&
              isSuperAdmin &&
              selectedCities.length === 0)
          }
          className="w-full bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
        >
          {loading ? (
            t("saving") || "Saving..."
          ) : (
            <>
              {t("create")} {t("user")}
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default CreateAdmin;
